import { Injectable } from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateTime } from "luxon";

const TYPED_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(?:([ap])\.?\s*m\.?)?$/i;

/** Reads a typed `H:mm[:ss]` or `h:mm am/pm` time, or returns null when the text is not a valid time. */
export function parseTypedTime(text: string): { hour: number; minute: number; second: number } | null {
  const match = TYPED_TIME.exec(text.trim());
  if (!match) {
    return null;
  }
  let hour = +match[1];
  const minute = +match[2];
  const second = match[3] ? +match[3] : 0;
  const meridiem = match[4]?.toLowerCase();
  if (hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  if (meridiem) {
    // Also accepts the field's own "HH:mm a" display (e.g. "14:30 PM", "00:30 AM") so editing it re-parses.
    const twelveHour = hour >= 1 && hour <= 12;
    const matchesDisplay = (hour === 0 && meridiem === "a") || (hour >= 13 && meridiem === "p");
    if (!twelveHour && !matchesDisplay) {
      return null;
    }
    if (twelveHour) {
      hour = (hour % 12) + (meridiem === "p" ? 12 : 0);
    }
  }
  return { hour, minute, second };
}

/** Luxon adapter whose time parsing is strict, so a typed time is either read exactly or rejected. */
@Injectable()
export class TimeEntryDateAdapter extends LuxonDateAdapter {
  override parseTime(value: unknown, parseFormat: string | string[]): DateTime | null {
    if (typeof value !== "string") {
      return super.parseTime(value, parseFormat);
    }
    if (!value.trim()) {
      return null;
    }
    const time = parseTypedTime(value);
    return time ? this.today().set({ ...time, millisecond: 0 }) : this.invalid();
  }
}
